import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import Table from 'rc-table';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import 'rc-table/assets/index.css';
import Tooltip from '@src/components/Tooltip';
import { useTranslation } from 'react-i18next';
import Typography from '@src/new-components/Typography';
import { useSelector } from 'react-redux';
import NotificationDialogV3 from '@src/components/NotificationDialogV3';
import { Button } from '@mui/material';
import { MERGE_PRODUCT_DURATION } from '@src/constants/package';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useLocation from '@src/hooks/useLocation';
import ChangeDuration from './ChangeDuration';
import Package from './Package';
import {
  MobileActionContainer,
  PackagesComparisonContainer,
  TabButton,
  TabsContainer,
} from './index.style';
import Header from './Header';
import creditsFeatures from './Groups/Credits';
import voiceFeatures from './Groups/Voices';
import ttsFeatures from './Groups/TTS';
import voiceCloningFeatures from './Groups/VoiceCloning';
import projectFeatures, { projectNewFeatures } from './Groups/Project';
import dubbingFeatures from './Groups/Dubbing';
import apiFeatures, { apiNewFeatures } from './Groups/API';
import deviceFeatures from './Groups/Device';
import ButtonBuyPackage from './ButtonBuyPackage';

const COLUMN_WIDTH = 266;
const TABLE_SCROLL_WIDTH = 1152;

const ICON_STYLE = {
  expand: { fontSize: '24px', color: '#1E164ECC' },
  check: { fontSize: '20px', color: '#16B364' },
  close: { fontSize: '20px', color: '#1E164EA3' },
};

const renderCellValue = (value) => {
  if (typeof value === 'boolean') {
    return value ? (
      <CheckCircleRoundedIcon style={ICON_STYLE.check} />
    ) : (
      <CloseRoundedIcon style={ICON_STYLE.close} />
    );
  }
  return (
    <Typography variant="body-14-regular" color="#1C1C1E">
      {value}
    </Typography>
  );
};

const getRowClassName = (record) => {
  if (record.type === 'group') {
    return 'group-row';
  }
  if (record.type === 'sub-group') {
    return 'sub-group-row';
  }

  return record.child ? `group-row-child` : `row-feature`;
};

const renderInfoIcon = (onClick) => (
  <div
    role="button"
    tabIndex={0}
    style={{
      display: 'flex',
      alignItems: 'center',
    }}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
  >
    <InfoOutlinedIcon style={{ fontSize: '14px', color: '#1E164EA3' }} />
  </div>
);

const renderGroupLabel = (value, isOpen, tooltipContent, onClickTooltip) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    }}
  >
    <Typography
      variant="body-16-bold"
      color="#1C1C1E"
      style={{
        textAlign: 'left',
      }}
    >
      {value}
    </Typography>
    {tooltipContent && (
      <Tooltip textAlign="left" arrow placement="top" title={tooltipContent}>
        {renderInfoIcon(onClickTooltip)}
      </Tooltip>
    )}
    {isOpen ? (
      <ExpandLessRoundedIcon style={ICON_STYLE.expand} />
    ) : (
      <ExpandMoreRoundedIcon style={ICON_STYLE.expand} />
    )}
  </div>
);

const PackagesComparison = forwardRef(
  (
    {
      saveUpToPercent,
      onChangeTab,
      activeGranularity,
      isMobile,
      canUseVouchers,
      currentPackages,
      packages,
      numberOfElements,
      totalVoucher,
      newPricingOverviewEnabled,
    },
    ref,
  ) => {
    const { t, i18n } = useTranslation();
    const { language } = i18n;
    const tableRef = useRef(null);
    const bottomRef = useRef(null);
    const { openSidebar } = useSelector((state) => state.layout);
    const { user } = useSelector((state) => state.auth);
    const { ip } = useLocation();
    const { getFeatureValue } = useFeatureFlags();
    const apiTtsSttLaunchEnabled = getFeatureValue(
      FEATURE_KEYS.LAUNCH_API_TTS_STT,
      {
        userId: user?.id,
        email: user?.email,
        phoneNumber: user?.phoneNumber,
        ip,
      },
    );
    const aiRewriteEnabled = getFeatureValue(FEATURE_KEYS.AI_REWRITE, {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
      ip,
    });
    const aiTranslateEnabled = getFeatureValue(FEATURE_KEYS.AI_TRANSLATE, {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
      ip,
    });
    const aiPresentationProjectEnabled = getFeatureValue(
      FEATURE_KEYS.PRESENTATION_PROJECTS,
      {
        userId: user?.id,
        email: user?.email,
        phoneNumber: user?.phoneNumber,
        ip,
      },
    );
    const transcriptAudioEnabled = getFeatureValue(
      FEATURE_KEYS.TRANSCRIPT_AUDIO,
      {
        userId: user?.id,
        email: user?.email,
        phoneNumber: user?.phoneNumber,
        ip,
      },
    );
    const sttAsyncConfig = getFeatureValue(FEATURE_KEYS.STT_ASYNC, {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
      ip,
    });
    const sttSyncConfig = getFeatureValue(FEATURE_KEYS.STT_SYNC, {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
      ip,
    });
    const sttStreamingConfig = getFeatureValue(FEATURE_KEYS.STT_STREAMING, {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
      ip,
    });
    const ttsAsyncConfig = getFeatureValue(FEATURE_KEYS.TTS_ASYNC, {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
      ip,
    });
    const ttsSyncConfig = getFeatureValue(FEATURE_KEYS.TTS_SYNC, {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
      ip,
    });
    const ttsStreamingConfig = getFeatureValue(FEATURE_KEYS.TTS_STREAMING, {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
      ip,
    });

    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [openPopupTooltip, setOpenPopupTooltip] = useState(false);
    const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);

    const SORT_PACKGES = ['BASIC', 'STANDARD', 'SPECIAL', 'VIP', 'CUSTOM'];
    const sortedPackages = useMemo(
      () =>
        [...currentPackages].sort(
          (a, b) =>
            SORT_PACKGES.indexOf(a.level) - SORT_PACKGES.indexOf(b.level),
        ),
      [currentPackages],
    );

    const [activeTab, setActiveTab] = useState(sortedPackages?.[0]?.id);

    const activePackage = useMemo(
      () =>
        sortedPackages.find((p) => p.id === activeTab) || sortedPackages?.[0],
      [sortedPackages, activeTab],
    );

    useEffect(() => {
      setActiveTab(sortedPackages?.[0]?.id);
    }, [sortedPackages]);

    const creditsGroup = useMemo(() => {
      const features = creditsFeatures({
        packages: isMobile ? [activePackage] : sortedPackages,
        activePackages: packages,
        canUseVouchers,
        totalVoucher,
      });
      return {
        key: 'credits',
        label: t('credits'),
        children: [
          {
            ...features.creditsUsed,
            label: t(features.creditsUsed.label),
          },
          {
            ...features.purchaseAdditionalCredits,
            label: t(features.purchaseAdditionalCredits.label),
          },
        ],
      };
    }, [
      isMobile,
      activePackage,
      sortedPackages,
      t,
      canUseVouchers,
      totalVoucher,
    ]);

    const voiceFeaturesGroup = useMemo(() => {
      const features = voiceFeatures({
        packages: isMobile ? [activePackage] : sortedPackages,
        canUseVouchers,
        totalVoucher,
      });
      return {
        key: 'voicesFeature',
        label: t('voicesFeature'),
        tooltipContent: 'noteTooltipVoices',
        children: [
          {
            ...features.basicVoice,
            label: t(features.basicVoice.label),
          },
          {
            ...features.vietnameseBasicVoice,
            label: t(features.vietnameseBasicVoice.label),
          },
          {
            ...features.globalBasicVoice,
            label: t(features.globalBasicVoice.label),
          },
          {
            ...features.advancedVoice,
            label: t(features.advancedVoice.label),
          },
          {
            ...features.vietnameseAdvancedVoice,
            label: t(features.vietnameseAdvancedVoice.label),
          },
          {
            ...features.globalAdvancedVoice,
            label: t(features.globalAdvancedVoice.label),
          },
          {
            ...features.premiumVoice,
            label: t(features.premiumVoice.label),
          },
          {
            ...features.vietnamesePremiumVoice,
            label: t(features.vietnamesePremiumVoice.label),
          },
          {
            ...features.globalPremiumVoice,
            label: t(features.globalPremiumVoice.label),
          },
          {
            ...features.communityVoiceLibrary,
            label: t(features.communityVoiceLibrary.label),
          },
        ],
      };
    }, [
      isMobile,
      activePackage,
      sortedPackages,
      t,
      canUseVouchers,
      totalVoucher,
    ]);

    const ttsGroup = useMemo(() => {
      const features = ttsFeatures({
        packages: isMobile ? [activePackage] : sortedPackages,
      });

      return {
        key: 'tts',
        label: t('tts'),
        children: [
          {
            ...features.preview,
            label: t(features.preview.label),
            tooltipContent: t(features.preview.tooltipContent),
          },
          {
            ...features.aiEditorPronunciationCheck,
            label: t(features.aiEditorPronunciationCheck.label),
          },
          {
            ...features.noWatermark,
            label: t(features.noWatermark.label),
          },
        ],
      };
    }, [isMobile, activePackage, sortedPackages, t]);

    const voiceCloningGroup = useMemo(() => {
      const features = voiceCloningFeatures({
        packages: isMobile ? [activePackage] : sortedPackages,
        activeGranularity,
      });
      return {
        key: 'voice-cloning',
        label: t('voiceCloningV2'),
        children: [
          {
            ...features.instantVoiceClones,
            label: t(features.instantVoiceClones.label),
          },
          {
            ...features.professionalVoiceClones,
            label: t(features.professionalVoiceClones.label),
          },
          {
            ...features.purchaseAdditionalProVoice,
            label: t(features.purchaseAdditionalProVoice.label),
          },
          {
            ...features.publishVoicesCommunity,
            label: t(features.publishVoicesCommunity.label),
          },
          {
            ...features.monetizeCommunityVoices,
            label: t(features.monetizeCommunityVoices.label),
          },
        ],
      };
    }, [isMobile, activePackage, sortedPackages, t, activeGranularity]);

    const projectGroup = useMemo(() => {
      const hasNewFeatures =
        aiRewriteEnabled ||
        aiTranslateEnabled ||
        aiPresentationProjectEnabled ||
        transcriptAudioEnabled;

      if (hasNewFeatures) {
        const features = projectNewFeatures({
          packages: isMobile ? [activePackage] : sortedPackages,
        });
        return {
          key: 'project',
          label: t('project'),
          children: [
            {
              ...features.noWatermark,
              label: t(features.noWatermark.label),
            },
            {
              ...features.concurrentProjects,
              label: t(features.concurrentProjects.label),
            },
            {
              ...features.numberSpeaker,
              label: t(features.numberSpeaker.label),
            },
            ...(aiRewriteEnabled
              ? [
                  {
                    ...features.aiRewrite,
                    label: t(features.aiRewrite.label),
                  },
                ]
              : []),
            ...(aiTranslateEnabled
              ? [
                  {
                    ...features.aiTranslation,
                    label: t(features.aiTranslation.label),
                  },
                ]
              : []),
            ...(aiPresentationProjectEnabled
              ? [
                  {
                    ...features.slideDubbing,
                    label: t(features.slideDubbing.label),
                    children: features.slideDubbing.children.map((child) => ({
                      ...child,
                      label: `• ${t(child.label)}`,
                    })),
                  },
                ]
              : []),
            ...(transcriptAudioEnabled
              ? [
                  {
                    ...features.transcriptAudio,
                    label: t(features.transcriptAudio.label),
                  },
                ]
              : []),
          ],
        };
      }

      const features = projectFeatures({
        packages: isMobile ? [activePackage] : sortedPackages,
      });
      return {
        key: 'project',
        label: t('project'),
        children: [
          {
            ...features.noWatermark,
            label: t(features.noWatermark.label),
          },
          {
            ...features.concurrentProjects,
            label: t(features.concurrentProjects.label),
          },
          {
            ...features.numberSpeaker,
            label: t(features.numberSpeaker.label),
          },
          {
            ...features.srtFileDuration,
            label: t(features.srtFileDuration.label),
          },
        ],
      };
    }, [
      isMobile,
      activePackage,
      sortedPackages,
      t,
      aiRewriteEnabled,
      aiTranslateEnabled,
      aiPresentationProjectEnabled,
      transcriptAudioEnabled,
    ]);

    const dubbingGroup = useMemo(() => {
      const features = dubbingFeatures({
        packages: isMobile ? [activePackage] : sortedPackages,
      });
      return {
        key: 'dubbing',
        label: t('dubbingV2'),
        children: [
          {
            ...features.noWatermark,
            label: t(features.noWatermark.label),
          },
          {
            ...features.srtFileDuration,
            label: t(features.srtFileDuration.label),
          },
        ],
      };
    }, [isMobile, activePackage, sortedPackages, t]);

    const apiGroup = useMemo(() => {
      if (apiTtsSttLaunchEnabled) {
        const features = apiNewFeatures({
          packages: isMobile ? [activePackage] : sortedPackages,
          canUseVouchers,
          totalVoucher,
          sttAsyncConfig,
          sttSyncConfig,
          sttStreamingConfig,
          ttsAsyncConfig,
          ttsSyncConfig,
          ttsStreamingConfig,
        });

        const mapSubGroupChildren = (subGroup) => ({
          ...subGroup,
          label: t(subGroup.label),
          children: subGroup.children.map((child) => ({
            ...child,
            label: `• ${t(child.label, {
              blockDuration: subGroup.blockDuration,
            })}`,
          })),
        });

        return {
          key: 'api',
          label: t('apiOnly'),
          children: [
            {
              ...features.usageLimit,
              label: t(features.usageLimit.label),
              tooltipContent: features.usageLimit.tooltipContent,
            },
            ...(ttsAsyncConfig ? [mapSubGroupChildren(features.ttsBatch)] : []),
            ...(ttsSyncConfig
              ? [mapSubGroupChildren(features.ttsRealtime)]
              : []),
            ...(ttsStreamingConfig
              ? [mapSubGroupChildren(features.ttsStreaming)]
              : []),
            ...(sttAsyncConfig ? [mapSubGroupChildren(features.sttBatch)] : []),
            ...(sttSyncConfig
              ? [mapSubGroupChildren(features.sttRealtime)]
              : []),
            ...(sttStreamingConfig
              ? [mapSubGroupChildren(features.sttStreaming)]
              : []),
          ],
        };
      }

      const features = apiFeatures({
        packages: isMobile ? [activePackage] : sortedPackages,
        canUseVouchers,
        totalVoucher,
      });
      return {
        key: 'api',
        label: t('apiOnly'),
        children: [
          {
            ...features.usageLimit,
            label: t(features.usageLimit.label),
          },
          {
            ...features.concurrentAPIRequests,
            label: t(features.concurrentAPIRequests.label),
          },
          {
            ...features.purchaseAdditionalCCR,
            label: t(features.purchaseAdditionalCCR.label),
          },
        ],
      };
    }, [
      isMobile,
      activePackage,
      sortedPackages,
      t,
      canUseVouchers,
      totalVoucher,
      apiTtsSttLaunchEnabled,
      sttAsyncConfig,
      sttSyncConfig,
      sttStreamingConfig,
      ttsAsyncConfig,
      ttsSyncConfig,
      ttsStreamingConfig,
    ]);

    const deviceGroup = useMemo(() => {
      const features = deviceFeatures({
        packages: isMobile ? [activePackage] : sortedPackages,
      });
      return {
        key: 'device',
        label: t('deviceV2'),
        children: [
          {
            ...features.concurrentDevices,
            label: t(features.concurrentDevices.label),
          },
          {
            ...features.purchaseAdditionalConcurrentDevices,
            label: t(features.purchaseAdditionalConcurrentDevices.label),
          },
        ],
      };
    }, [isMobile, activePackage, sortedPackages, t]);

    const groups = [
      {
        ...creditsGroup,
      },
      {
        ...voiceFeaturesGroup,
      },
      {
        ...ttsGroup,
      },
      {
        ...voiceCloningGroup,
      },
      {
        ...projectGroup,
      },
      {
        ...dubbingGroup,
      },
      {
        ...apiGroup,
      },
      {
        ...deviceGroup,
      },
    ];

    const [openGroups, setOpenGroups] = useState(groups.map((g) => g.key));

    const columns = () => [
      {
        title: (
          <ChangeDuration
            saveUpToPercent={saveUpToPercent}
            onChangeTab={onChangeTab}
            activeGranularity={activeGranularity}
            disabled
          />
        ),
        dataIndex: 'label',
        key: 'label',
        width: isMobile ? '50%' : COLUMN_WIDTH,
        fixed: isMobile ? undefined : 'left',
        colSpan: (record) => record.type === 'group' && isMobile && 2,
        render: (value, record) => {
          if (record.type === 'group') {
            const isOpen = openGroups.includes(record.key);
            return {
              children: renderGroupLabel(
                value,
                isOpen,
                record.tooltipContent && (
                  <div
                    /* eslint-disable-next-line react/no-danger */
                    dangerouslySetInnerHTML={{
                      __html: t(record.tooltipContent),
                    }}
                  />
                ),
                () => setOpenPopupTooltip(t(record.tooltipContent)),
              ),
              props: {
                colSpan: isMobile ? 2 : undefined,
              },
            };
          }

          if (record.type === 'sub-group') {
            return (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <Typography
                  variant="body-14-regular"
                  color="#1C1C1E"
                  style={{ textAlign: 'left' }}
                >
                  {value}
                </Typography>
                {record.tooltipContent && (
                  <Tooltip
                    textAlign="left"
                    arrow
                    placement="top"
                    title={
                      <div
                        /* eslint-disable-next-line react/no-danger */
                        dangerouslySetInnerHTML={{
                          __html: t(record.tooltipContent),
                        }}
                      />
                    }
                  >
                    {renderInfoIcon(() =>
                      setOpenPopupTooltip(t(record.tooltipContent)),
                    )}
                  </Tooltip>
                )}
              </div>
            );
          }

          if (record.tooltipContent) {
            return (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <Typography variant="body-14-regular" color="#1C1C1E">
                  {value}
                </Typography>
                <Tooltip
                  arrow
                  placement="top"
                  textAlign="left"
                  title={
                    <div
                      /* eslint-disable-next-line react/no-danger */
                      dangerouslySetInnerHTML={{
                        __html: t(record.tooltipContent),
                      }}
                    />
                  }
                >
                  {renderInfoIcon(() =>
                    setOpenPopupTooltip(t(record.tooltipContent)),
                  )}
                </Tooltip>
              </div>
            );
          }
          return (
            <Typography variant="body-14-regular" color="#1C1C1E">
              {value}
            </Typography>
          );
        },
      },
      ...(isMobile ? [activePackage] : sortedPackages).map((pkg, index) => ({
        title: (
          <Package
            data={pkg}
            packages={packages}
            totalVoucher={totalVoucher}
            isMobile={isMobile}
          />
        ),
        width: isMobile ? '50%' : COLUMN_WIDTH,
        key: pkg?.id,
        align: 'center',
        render: (_, record) => {
          if (!record.values) return null;
          return renderCellValue(record.values[index]);
        },
      })),
    ];

    const toggleGroup = (key) => {
      setOpenGroups((prev) =>
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
      );
    };

    const handleRowClick = (record) => {
      if (record.type === 'group') {
        return {
          onClick: () => toggleGroup(record.key),
        };
      }
      return {};
    };

    const dataSource = useMemo(() => {
      const rows = [];

      groups.forEach((group) => {
        rows.push({
          key: group.key,
          type: 'group',
          label: group.label,
          className: 'group-row',
          tooltipContent: group.tooltipContent,
        });

        if (openGroups.includes(group.key)) {
          group.children.forEach((item) => {
            if (item.type === 'sub-group') {
              rows.push({
                key: item.key,
                type: 'sub-group',
                label: item.label,
                tooltipContent: item.tooltipContent,
                values: item.values,
              });
              item.children?.forEach((subItem) => {
                rows.push({
                  key: subItem.key,
                  type: 'feature',
                  label: subItem.label,
                  values: subItem.values,
                  child: true,
                  tooltipContent: subItem.tooltipContent,
                });
              });
            } else {
              rows.push({
                key: item.key,
                type: 'feature',
                label: item.label,
                values: item.values,
                child: item.child,
                tooltipContent: item.tooltipContent,
              });
            }
          });
        }
      });

      return rows;
    }, [openGroups, groups]);

    // Handle scroll buttons
    const updateScrollButtons = () => {
      if (tableRef.current && !isMobile) {
        const scrollContainer =
          tableRef.current.querySelector('.rc-table-body');
        if (scrollContainer) {
          const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;
          setCanScrollLeft(scrollLeft > 0);
          setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
        }
      }
    };

    const handleScrollLeft = () => {
      if (tableRef.current) {
        const scrollContainer =
          tableRef.current.querySelector('.rc-table-body');
        if (scrollContainer) {
          scrollContainer.scrollBy({ left: -COLUMN_WIDTH, behavior: 'smooth' });
        }
      }
    };

    const handleScrollRight = () => {
      if (tableRef.current) {
        const scrollContainer =
          tableRef.current.querySelector('.rc-table-body');
        if (scrollContainer) {
          scrollContainer.scrollBy({ left: COLUMN_WIDTH, behavior: 'smooth' });
        }
      }
    };

    // Auto scroll to STANDARD package
    useEffect(() => {
      if (isMobile) return;
      const scrollToSecondColumn = () => {
        if (tableRef.current) {
          const scrollContainer =
            tableRef.current.querySelector('.rc-table-body');
          if (scrollContainer) {
            // Scroll to COLUMN_WIDTH (266px) to show the second column
            scrollContainer.scrollLeft = COLUMN_WIDTH;
            updateScrollButtons();
          }
        }
      };

      const timer = setTimeout(scrollToSecondColumn, 100);
      return () => clearTimeout(timer); // eslint-disable-line consistent-return
    }, [sortedPackages, isMobile, openSidebar]);

    useEffect(() => {
      window.addEventListener('resize', updateScrollButtons);
      updateScrollButtons();
      return () => {
        window.removeEventListener('resize', updateScrollButtons);
      };
    }, [updateScrollButtons]);

    useEffect(() => {
      if (isMobile) return;

      const scrollContainer = tableRef.current?.querySelector('.rc-table-body');
      if (scrollContainer) {
        scrollContainer.addEventListener('scroll', updateScrollButtons);
        updateScrollButtons();

        // eslint-disable-next-line consistent-return
        return () => {
          scrollContainer.removeEventListener('scroll', updateScrollButtons);
        };
      }
    }, [isMobile, tableRef?.current, openSidebar]);

    useEffect(() => {
      if (!isMobile || !bottomRef.current) return;

      const checkScrollEnd = () => {
        const container = bottomRef.current;
        if (container) {
          const endScroll =
            container.scrollLeft + container.clientWidth >=
            container.scrollWidth - 1;

          setIsScrolledToEnd(endScroll);
        }
      };

      const container = bottomRef.current;
      container.addEventListener('scroll', checkScrollEnd);
      checkScrollEnd();

      // eslint-disable-next-line consistent-return
      return () => {
        container.removeEventListener('scroll', checkScrollEnd);
      };
    }, [isMobile, bottomRef?.current, activeGranularity]);

    useEffect(() => {
      if (activeGranularity !== MERGE_PRODUCT_DURATION.MONTHLY) {
        setIsScrolledToEnd(false);
      }
    }, [activeGranularity]);

    return (
      <PackagesComparisonContainer
        ref={tableRef}
        numberOfElements={numberOfElements > 4 ? 4 : numberOfElements}
      >
        <Header
          isMobile={isMobile}
          handleScrollLeft={handleScrollLeft}
          handleScrollRight={handleScrollRight}
          canScrollLeft={canScrollLeft}
          canScrollRight={canScrollRight}
        />
        <div ref={ref}>
          <Table
            className="packages-comparison-table"
            columns={columns()}
            data={dataSource}
            rowKey="key"
            rowClassName={getRowClassName}
            tableLayout="fixed"
            onRow={handleRowClick}
            sticky={
              isMobile
                ? false
                : { offsetHeader: newPricingOverviewEnabled ? 65 : 0 }
            }
            scroll={{ x: isMobile ? undefined : TABLE_SCROLL_WIDTH }}
          />
          {isMobile && (
            <MobileActionContainer
              isScrolledToEnd={isScrolledToEnd}
              isMonthPackage={
                activeGranularity === MERGE_PRODUCT_DURATION.MONTHLY
              }
            >
              <TabsContainer ref={bottomRef}>
                {sortedPackages?.map((pkg) => (
                  <TabButton
                    type="button"
                    key={pkg.id}
                    onClick={() => setActiveTab(pkg.id)}
                    isActive={activeTab === pkg.id}
                  >
                    {pkg.name?.[language]}
                  </TabButton>
                ))}
              </TabsContainer>
              <ButtonBuyPackage
                data={activePackage}
                packages={packages}
                isHighlightPackage
              />
            </MobileActionContainer>
          )}
        </div>

        {openPopupTooltip && (
          <NotificationDialogV3
            name={t('detailedInformation')}
            title={t('detailedInformation')}
            description={openPopupTooltip}
            open={openPopupTooltip && isMobile}
            onClose={() => setOpenPopupTooltip(false)}
            variant="null"
            actionComponent={
              <Button
                onClick={() => setOpenPopupTooltip(false)}
                className="btn-close-popup"
                variant="contained"
              >
                {t('gotIt')}
              </Button>
            }
          />
        )}
      </PackagesComparisonContainer>
    );
  },
);

export default PackagesComparison;
